// lib/services/compute/animation_slot.ts

import BaseSlot, { SlotState } from "./base_slot";

export const ANIMATION_STATE_BYTES = 32;

/**
 * Per-frame slot for the GPU animation pipeline.
 *
 * Holds the per-frame uniform buffer (current/next clip indices, times,
 * blend alpha) and the joint palette buffer that the compute pass writes
 * and the render pass reads.
 *
 * Lifecycle: Idle -> Recording (writer begins) -> Ready (writer publishes).
 * After lease/release, the pool keeps the slot Ready (PostReleasePolicy.KeepReady)
 * so the render path can re-lease the same data across frames when the
 * compute path has not produced something newer.
 */
export default class AnimationSlot extends BaseSlot {
  animation_state_buffer: GPUBuffer | null = null;
  joint_palette_buffer: GPUBuffer | null = null;

  constructor() {
    super(false);
  }

  /**
   * Allocate the slot's GPU buffers. Called once per slot during pool
   * initialization. The slot owns the buffers and destroys them on recycle.
   */
  allocate_buffers(
    device: GPUDevice,
    key: string,
    slot_index: number,
    joint_count: number
  ): void {
    this.animation_state_buffer = device.createBuffer({
      label: `${key}_anim_state_${slot_index}`,
      size: ANIMATION_STATE_BYTES,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    this.joint_palette_buffer = device.createBuffer({
      label: `${key}_joint_palette_${slot_index}`,
      size: joint_count * 64,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
    });
  }

  /**
   * Producer entry point: transition Idle -> Recording.
   * Returns false if the slot is not Idle.
   */
  begin_record(): boolean {
    if (this.state !== SlotState.Idle) return false;
    this.transition_to(SlotState.Recording);
    return true;
  }

  /**
   * Producer commit: Recording -> Ready with sequence and timestamp.
   * The pool's publish_slot path normally drives this, but direct
   * transitions from inside producer code are also supported.
   */
  mark_ready(sequence: number, timestampSec: number): void {
    this.sequence = sequence;
    this.timestampSec = timestampSec;
    this.transition_to(SlotState.Ready);
  }

  /**
   * Producer cancel: Recording -> Idle, dropping any partial work.
   */
  cancel_record(): void {
    if (this.state === SlotState.Recording) {
      this.transition_to(SlotState.Idle);
    }
  }

  /**
   * Recycle clears CPU bookkeeping fields and destroys GPU buffers.
   * Called by the pool during shutdown. Per-frame recycling between
   * uses keeps the buffers alive and only clears state.
   */
  recycle(): void {
    super.recycle();
  }

  /**
   * Explicit destruction path used during pool shutdown.
   */
  destroy_buffers(): void {
    if (this.animation_state_buffer) {
      this.animation_state_buffer.destroy();
      this.animation_state_buffer = null;
    }
    if (this.joint_palette_buffer) {
      this.joint_palette_buffer.destroy();
      this.joint_palette_buffer = null;
    }
  }
}
